import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

// Pagina di cortesia mostrata a tutti i visitatori non-admin quando la manutenzione è attiva.
// Il blocco reale è lato server (middleware 503): questa è solo la presentazione.
// Opzionale: countdown all'apertura (impostata dall'admin) + raccolta email "avvisami quando aprite".
export default function Maintenance() {
  const [openingAt, setOpeningAt] = useState(null);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [now, setNow] = useState(Date.now());
  // Sondaggio "che giochi vorresti in negozio": email obbligatoria, multi-scelta, un voto per email
  const [survey, setSurvey] = useState({ tcg: [], esports: [] });
  const [surveyForm, setSurveyForm] = useState({ nome: "", cognome: "", email: "", indirizzo: "", citta: "", suggestion: "" });
  const [picked, setPicked] = useState({ tcg: new Set(), esports: new Set() });
  const [surveyDone, setSurveyDone] = useState(false);
  const [surveySending, setSurveySending] = useState(false);

  useEffect(() => {
    api.get("/maintenance-status").then((r) => setOpeningAt(r.data.opening_at || null)).catch(() => {});
    api.get("/game-survey/options").then((r) => setSurvey(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!openingAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [openingAt]);

  const diff = openingAt ? Math.max(0, new Date(openingAt).getTime() - now) : null;
  const countdown = diff !== null && diff > 0
    ? {
        giorni: Math.floor(diff / 86400000),
        ore: Math.floor((diff % 86400000) / 3600000),
        minuti: Math.floor((diff % 3600000) / 60000),
        secondi: Math.floor((diff % 60000) / 1000),
      }
    : null;

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Inserisci una email valida");
    setSending(true);
    try {
      const { data } = await api.post("/notify-launch", { email });
      setDone(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const togglePick = (kind, game) => {
    setPicked((p) => {
      const next = new Set(p[kind]);
      if (next.has(game)) next.delete(game); else next.add(game);
      return { ...p, [kind]: next };
    });
  };

  const submitSurvey = async (e) => {
    e.preventDefault();
    const f = { nome: surveyForm.nome.trim(), cognome: surveyForm.cognome.trim(), email: surveyForm.email.trim(), indirizzo: surveyForm.indirizzo.trim(), citta: surveyForm.citta.trim() };
    if (!f.nome || !f.cognome || !f.indirizzo || !f.citta) return toast.error("Servono nome, cognome, indirizzo e città");
    if (!f.email.includes("@")) return toast.error("Lascia la tua email: ti avvisiamo all'apertura");
    if (!picked.tcg.size && !picked.esports.size) return toast.error("Scegli almeno un gioco");
    setSurveySending(true);
    try {
      const { data } = await api.post("/game-survey", { ...f, tcg: [...picked.tcg], esports: [...picked.esports], suggestion: surveyForm.suggestion.trim() });
      setSurveyDone(true);
      toast.success(data.message);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail);
      toast.error(msg);
      if (msg.includes("già votato")) setSurveyDone(true);
    } finally {
      setSurveySending(false);
    }
  };

  const surveyInputCls = "w-full bg-[#0D0D0E] border border-white/10 focus:border-red-600/60 rounded-full px-5 py-3 text-base sm:text-sm text-white placeholder-zinc-600 outline-none transition-colors";

  const chipCls = (active) =>
    `text-[11px] font-display font-bold uppercase tracking-wide border rounded-full px-3.5 py-2 transition-all active:scale-[0.97] ${
      active ? "bg-red-600 border-red-600 text-white" : "border-white/15 text-zinc-400 hover:border-red-600/60 hover:text-white"
    }`;

  return (
    <main data-testid="maintenance-page" className="fixed inset-0 z-[100] bg-[#050505] overflow-y-auto px-6 text-center">
      <img src="/logo-rosa.png" alt="" aria-hidden="true" className="fixed top-1/2 -translate-y-1/2 pointer-events-none opacity-[0.10] h-[115vh] w-auto max-w-none -left-[52vh] max-md:h-[70vh] max-md:-left-[32vh]" />
      <div className="fixed inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 45% at 50% 38%, rgba(220,38,38,0.09), transparent 70%)" }} />
      <div className="relative min-h-full flex flex-col items-center justify-center py-14">
      <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="relative flex flex-col items-center w-full max-w-md">
        <motion.img
          src="/logo-rosa.png"
          alt="Rosa dei venti WANTED"
          className="w-24 h-24 sm:w-32 sm:h-32 object-contain"
          animate={{ rotate: 360 }}
          transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
        />
        <img src="/logo-scritta.png" alt="WANTED" className="h-7 sm:h-9 w-auto object-contain mt-8" />
        <h1 className="mt-10 text-2xl sm:text-3xl font-display font-extrabold uppercase tracking-tight text-white leading-snug">
          Stiamo preparando tutto
        </h1>
        <p className="mt-4 text-sm sm:text-base font-mono text-zinc-400 leading-relaxed">
          Torneremo presto online.
        </p>

        {countdown && (
          <div data-testid="maintenance-countdown" className="mt-8 flex items-start gap-3">
            {[
              [countdown.giorni, "giorni"],
              [countdown.ore, "ore"],
              [countdown.minuti, "min"],
              [countdown.secondi, "sec"],
            ].map(([v, label]) => (
              <div key={label} className="flex flex-col items-center">
                <span className="w-14 sm:w-16 py-2.5 rounded-lg border border-white/10 bg-white/[0.03] font-display font-extrabold text-xl sm:text-2xl text-white tabular-nums">
                  {String(v).padStart(2, "0")}
                </span>
                <span className="mt-1.5 text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-600">{label}</span>
              </div>
            ))}
          </div>
        )}

        {done ? (
          <p data-testid="maintenance-subscribed" className="mt-8 text-sm font-mono text-emerald-400">
            Fatto! Ti avvisiamo appena apriamo.
          </p>
        ) : (
          <form data-testid="maintenance-notify-form" onSubmit={subscribe} className="mt-8 w-full">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-3">Vuoi sapere quando apriamo?</p>
            <div className="flex gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="La tua email"
                data-testid="maintenance-email-input"
                className="flex-1 min-w-0 bg-[#0D0D0E] border border-white/10 focus:border-red-600/60 rounded-full px-5 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors"
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="maintenance-notify-button"
                className="shrink-0 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white font-display font-bold uppercase tracking-wide text-xs px-5 py-3 rounded-full transition-all active:scale-[0.97]"
              >
                {sending ? "..." : "Avvisami"}
              </button>
            </div>
            <p className="mt-2.5 text-[10px] font-mono text-zinc-700">
              Niente spam: solo l'avviso di apertura. <Link to="/privacy" className="underline hover:text-zinc-500">Privacy</Link>
            </p>
          </form>
        )}

        <div className="mt-8 flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          <span className="w-1.5 h-1.5 rounded-full bg-red-500/60 animate-pulse" style={{ animationDelay: "0.3s" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-red-500/30 animate-pulse" style={{ animationDelay: "0.6s" }} />
        </div>
      </motion.div>

      {(survey.tcg.length > 0 || survey.esports.length > 0) && (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }} data-testid="survey-block" className="relative mt-14 w-full max-w-2xl border-t border-white/10 pt-10 pb-4">
          <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-red-500 mb-2">Sondaggio lampo</p>
          <h2 className="text-xl sm:text-2xl font-display font-extrabold uppercase tracking-tight text-white">Quali giochi vorresti trovare da noi?</h2>
          <p className="mt-3 text-xs sm:text-sm font-mono text-zinc-500 leading-relaxed">
            Lascia i tuoi dati (ti avvisiamo all'apertura) e scegli tutti i giochi che vuoi, carte ed eSports. Una sola votazione per email.
          </p>
          {surveyDone ? (
            <p data-testid="survey-done" className="mt-8 text-sm font-mono text-emerald-400">
              Grazie! Voto registrato: ti avvisiamo appena apriamo.
            </p>
          ) : (
            <form data-testid="survey-form" onSubmit={submitSurvey} className="mt-7 text-left">
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  required
                  value={surveyForm.nome}
                  onChange={(e) => setSurveyForm({ ...surveyForm, nome: e.target.value })}
                  placeholder="Nome"
                  data-testid="survey-nome-input"
                  className={surveyInputCls}
                />
                <input
                  type="text"
                  required
                  value={surveyForm.cognome}
                  onChange={(e) => setSurveyForm({ ...surveyForm, cognome: e.target.value })}
                  placeholder="Cognome"
                  data-testid="survey-cognome-input"
                  className={surveyInputCls}
                />
              </div>
              <input
                type="email"
                required
                value={surveyForm.email}
                onChange={(e) => setSurveyForm({ ...surveyForm, email: e.target.value })}
                placeholder="La tua email"
                data-testid="survey-email-input"
                className={`mt-3 ${surveyInputCls}`}
              />
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <input
                  type="text"
                  required
                  value={surveyForm.indirizzo}
                  onChange={(e) => setSurveyForm({ ...surveyForm, indirizzo: e.target.value })}
                  placeholder="Indirizzo"
                  data-testid="survey-indirizzo-input"
                  className={surveyInputCls}
                />
                <input
                  type="text"
                  required
                  value={surveyForm.citta}
                  onChange={(e) => setSurveyForm({ ...surveyForm, citta: e.target.value })}
                  placeholder="Città"
                  data-testid="survey-citta-input"
                  className={surveyInputCls}
                />
              </div>
              <textarea
                value={surveyForm.suggestion}
                onChange={(e) => setSurveyForm({ ...surveyForm, suggestion: e.target.value })}
                placeholder="Vuoi aggiungere un gioco che non c'è in elenco o darci un suggerimento? Scrivilo qui! (facoltativo)"
                data-testid="survey-suggestion-input"
                rows={2}
                maxLength={500}
                className="mt-3 w-full bg-[#0D0D0E] border border-white/10 focus:border-red-600/60 rounded-2xl px-5 py-3 text-base sm:text-sm text-white placeholder-zinc-600 outline-none transition-colors resize-none"
              />
              <p className="mt-4 text-[11px] font-mono text-zinc-600 leading-relaxed text-center" data-testid="survey-privacy-note">
                I tuoi dati personali vengono usati solo per questo sondaggio e per avvisarti dell'apertura del negozio: non vengono venduti né ceduti a terzi.
              </p>
              {survey.tcg.length > 0 && (
                <div className="mt-6">
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-3">Giochi di carte</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {survey.tcg.map((g) => (
                      <button type="button" key={g} data-testid={`survey-tcg-${g}`} onClick={() => togglePick("tcg", g)} className={chipCls(picked.tcg.has(g))}>{g}</button>
                    ))}
                  </div>
                </div>
              )}
              {survey.esports.length > 0 && (
                <div className="mt-6">
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-3">eSports</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {survey.esports.map((g) => (
                      <button type="button" key={g} data-testid={`survey-esports-${g}`} onClick={() => togglePick("esports", g)} className={chipCls(picked.esports.has(g))}>{g}</button>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-7 text-center">
                <button
                  type="submit"
                  disabled={surveySending}
                  data-testid="survey-submit"
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white font-display font-bold uppercase tracking-wide text-xs px-8 py-3 rounded-full transition-all active:scale-[0.97]"
                >
                  {surveySending ? "Invio…" : `Invia il voto${picked.tcg.size + picked.esports.size ? ` (${picked.tcg.size + picked.esports.size})` : ""}`}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      )}
      <Link
        to="/login"
        data-testid="maintenance-admin-link"
        className="relative mt-12 text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-700 hover:text-zinc-400 transition-colors"
      >
        Accesso amministratore
      </Link>
      </div>
    </main>
  );
}
